import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DigitalEmployee, EmployeeCategory } from '../types';
import TeslaRobot from './TeslaRobot';
import TeslaEmployeeCard from './TeslaEmployeeCard';

interface CategoryBrowserProps {
  employees: DigitalEmployee[];
}

const CategoryBrowser: React.FC<CategoryBrowserProps> = ({ employees }) => {
  const [selectedCategory, setSelectedCategory] = useState<EmployeeCategory | null>(null);
  
  // Get all available categories
  const categories: EmployeeCategory[] = ['HR', 'PMO', 'Developer', 'Personal Assistant', 'RFP Developer'];
  
  // Filter employees by selected category
  const filteredEmployees = selectedCategory 
    ? employees.filter(employee => employee.category === selectedCategory)
    : [];
  
  // Get count of employees in each category
  const getCategoryCount = (category: EmployeeCategory): number => {
    return employees.filter(employee => employee.category === category).length;
  };
  
  // Get a unique color for each category
  const getCategoryColor = (category: EmployeeCategory): string => {
    const colorMap: Record<EmployeeCategory, string> = {
      'HR': '#4a6bff',
      'PMO': '#6e3adc',
      'Developer': '#00f7ff',
      'Personal Assistant': '#ff4757',
      'RFP Developer': '#ffbc42'
    };
    
    return colorMap[category];
  };
  
  return (
    <div className="category-browser">
      <div className="category-header">
        <h2 className="section-title">Browse by <span className="text-gradient">Category</span></h2>
        <p className="section-subtitle">Find AI employees specialized in different professional domains</p>
      </div>
      
      <div className="categories-container">
        <div className="categories-list">
          {categories.map(category => (
            <div 
              key={category}
              className={`category-card ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
              style={{ 
                '--category-color': getCategoryColor(category),
                '--category-glow': `0 0 15px ${getCategoryColor(category)}80`
              } as React.CSSProperties}
            >
              <div className="category-icon">
                <TeslaRobot width={50} height={50} />
              </div>
              <div className="category-info">
                <h3>{category}</h3>
                <span className="category-count">{getCategoryCount(category)} employees</span>
              </div>
              <div className="category-arrow">→</div>
            </div>
          ))}
        </div>
        
        <div className="category-results">
          {selectedCategory ? (
            <>
              <div className="results-header">
                <h3>
                  <span style={{ color: getCategoryColor(selectedCategory) }}>{selectedCategory}</span> Employees
                </h3>
                <button 
                  className="clear-category"
                  onClick={() => setSelectedCategory(null)}
                >
                  Clear
                </button>
              </div>
              
              {filteredEmployees.length > 0 ? (
                <div className="category-employees-grid">
                  {filteredEmployees.map(employee => (
                    <TeslaEmployeeCard 
                      key={employee.id} 
                      employee={employee} 
                      className="category-employee-card"
                    />
                  ))}
                </div>
              ) : (
                <div className="no-category-results">
                  <p>No employees found in this category</p>
                </div>
              )}
            </>
          ) : (
            <div className="select-category-prompt">
              <div className="prompt-icon">
                <TeslaRobot width={80} height={80} />
              </div>
              <p>Select a category to view specialized AI employees</p>
              <Link to="/search" className="browse-all-link">
                Or search all employees
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryBrowser;
